import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography,
  Avatar, Chip, Divider, List, ListItem, ListItemText, ListItemAvatar,
  TextField, Button, LinearProgress, Skeleton, IconButton, Tooltip,
} from '@mui/material';
import {
  People, CheckCircle, Cancel, HourglassEmpty, BeachAccess,
  Business, CalendarMonth, ArrowForward, Refresh,
  AccessTime, EventBusy,
} from '@mui/icons-material';
import { InvokeApi, apiList, getCachedDepartments } from '../services/ApiService';
import { Config } from '../config/Config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const StatCard = ({ title, value, icon, gradient, sub, onClick, loading }) => (
  <Card onClick={onClick} sx={{
    borderRadius: 3, cursor: onClick ? 'pointer' : 'default',
    background: gradient,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    transition: 'all 0.25s ease',
    overflow: 'hidden', position: 'relative',
    '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: '0 16px 48px rgba(0,0,0,0.22)' } : {},
  }}>
    <Box sx={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', top: -30, right: -30 }} />
    <Box sx={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: -20, left: -20 }} />
    <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: 13, mb: 0.5 }}>{title}</Typography>
          {loading ? (
            <Skeleton variant="text" width={60} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          ) : (
            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 900, lineHeight: 1.1 }}>{value ?? '—'}</Typography>
          )}
          {sub && <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, mt: 0.5 }}>{sub}</Typography>}
        </Box>
        <Avatar sx={{ background: 'rgba(255,255,255,0.2)', width: 52, height: 52, backdropFilter: 'blur(8px)' }}>
          {React.cloneElement(icon, { sx: { color: '#fff', fontSize: 26 } })}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const DeptBar = ({ dept, present, absent, late, half_day, leave, weekOff, total }) => {
  const pct = total > 0 ? Math.round((present / total) * 100) : 0;
  const color = pct >= 80 ? '#2e7d32' : pct >= 50 ? '#f57c00' : '#c62828';
  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#fafafa', border: '1px solid #f0f0f0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <Typography fontSize={13} fontWeight={700}>{dept}</Typography>
        </Box>
        <Typography fontSize={12} fontWeight={800} color={color}>{pct}% · {total} total</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 6, borderRadius: 4, background: '#e8eaf6', mb: 1,
        '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg,${color}99,${color})`, borderRadius: 4 },
      }} />
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {[
          { label: `Present ${present}`,  bg: '#e8f5e9', color: '#2e7d32' },
          { label: `Absent ${absent}`,    bg: '#ffebee', color: '#c62828' },
          { label: `Half Day ${half_day}`, bg: '#fff3e0', color: '#e65100' },
          { label: `Fine ${late}`,        bg: '#f3e5f5', color: '#6a1b9a' },
          { label: `Leave ${leave}`,      bg: '#fff3e0', color: '#e65100' },
          { label: `Week Off ${weekOff}`, bg: '#e0f2f1', color: '#00695c' },
        ].map(s => (
          <Chip key={s.label} label={s.label} size="small"
            sx={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: 10, height: 20 }} />
        ))}
      </Box>
    </Box>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHR = user?.role === 'HR';

  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [statsLoading, setStatsLoading] = useState(true);
  const [sideLoading, setSideLoading] = useState(true);

  const [stats, setStats] = useState({ present: 0, absent: 0, leave: 0, late: 0, weekOff: 0, total: 0 });
  const [deptStats, setDeptStats] = useState([]);
  const [deptCount, setDeptCount] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);

  // ── 2 API calls replace 5 + 6N + N calls ─────────────────────────────────
  // Call 1: getDailyAttendanceSummary  → all attendance rows + counts in 1 DB query
  // Call 2: getDeptartments            → dept list for count + per-dept employee totals
  const loadStats = useCallback(async () => {
    setStatsLoading(true);

    const formatted = dayjs(selectedDate).format('DD/MM/YYYY');

    const [summaryData, deptData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getDailyAttendanceSummary, { date: formatted, department: 'All' }),
      getCachedDepartments(Config.main_url).then(d => ({ department: d })),
    ]);

    // ── Counts from summary ───────────────────────────────────────────────
    const counts = summaryData?.counts || {};
    // Total = sum of all statuses (including Not Marked) — no extra call needed
    const total = Object.values(counts).reduce((a, b) => a + b, 0);

    setStats({
      present: counts['Present']  || 0,
      absent:  counts['Absent']   || 0,
      leave:   counts['Leave']    || 0,
      half_day:   counts['Half Day'] || 0,
      late:    counts['Late']     || 0,
      weekOff: counts['Week Off'] || 0,
      total,
    });

    const deptList = deptData?.department || [];
    setDeptCount(deptList.length);

    // ── Per-dept breakdown — built client-side from summary rows ──────────
    // summary already has all attendance rows grouped by status
    const deptMap = {};

    // Init all known depts with zeros
    deptList.forEach(d => {
      deptMap[d.department] = { present: 0, absent: 0, late: 0, half_day: 0, leave: 0, weekOff: 0, marked: 0 };
    });

    // Tally attendance rows
    const statusMap = {
      'Present':  'present',
      'Absent':   'absent',
      'Late':     'late',
      'Half Day': 'half_day',
      'Leave':    'leave',
      'Week Off': 'weekOff',
    };
    Object.entries(summaryData?.summary || {}).forEach(([status, rows]) => {
      if (status === 'Not Marked') return; // handled separately below
      const key = statusMap[status];
      if (!key) return;
      (rows || []).forEach(r => {
        const dept = r.department;
        if (!dept) return;
        if (!deptMap[dept]) deptMap[dept] = { present: 0, absent: 0, late: 0, half_day: 0, leave: 0, weekOff: 0, marked: 0 };
        deptMap[dept][key]++;
        deptMap[dept].marked++;
      });
    });

    // Add Not Marked rows to dept totals
    (summaryData?.summary?.['Not Marked'] || []).forEach(r => {
      const dept = r.department;
      if (!dept) return;
      if (!deptMap[dept]) deptMap[dept] = { present: 0, absent: 0, late: 0, half_day: 0, leave: 0, weekOff: 0, marked: 0 };
      // not marked doesn't add to any status bucket, just total
    });

    // Build final dept stats — total = marked + not-marked for that dept
    // We derive total per dept from the Not Marked rows + marked rows
    const deptNotMarked = {};
    (summaryData?.summary?.['Not Marked'] || []).forEach(r => {
      if (!r.department) return;
      deptNotMarked[r.department] = (deptNotMarked[r.department] || 0) + 1;
    });

    const deptStatsList = Object.entries(deptMap).map(([dept, v]) => {
      const notMarked = deptNotMarked[dept] || 0;
      const total = v.present + v.absent + v.late + v.half_day + v.leave + v.weekOff + notMarked;
      return { dept, ...v, total };
    }).filter(d => d.total > 0);

    setDeptStats(deptStatsList);
    setStatsLoading(false);
  }, [selectedDate]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Side panel: 2 parallel calls, runs once on mount ─────────────────────
  useEffect(() => {
    const loadSide = async () => {
      setSideLoading(true);
      const [leavesData, holidayData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getLeavesEmployees, { status: 'Pending' }),
        InvokeApi(Config.main_url + apiList.getHolidays, {}),
      ]);
      setPendingLeaves(leavesData?.leaves || []);
      const upcoming = (holidayData?.holidays || [])
        .filter(h => dayjs(h.date).isSame(dayjs(), 'day') || dayjs(h.date).isAfter(dayjs()))
        .slice(0, 5);
      setUpcomingHolidays(upcoming);
      setSideLoading(false);
    };
    loadSide();
  }, []);

  const isToday = selectedDate === dayjs().format('YYYY-MM-DD');

  return (
    <>
      {isHR ?
        <Grid container sx={{ alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h4" fontWeight={900} color="#1a237e">Welcome to Ashwayana Reality Group</Typography>
        </Grid>
      :
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={900} color="#1a237e">Ashwayana Reality Group</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>Management Panel</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              type="date" size="small" value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              label="Select Date"
              sx={{ minWidth: 170 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={loadStats} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Chip
            label={isToday ? 'Today' : dayjs(selectedDate).format('DD MMM YYYY')}
            sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }}
          />
        </Box>

        {/* Stat Cards */}
        <Grid container spacing={2} mb={3}>
          {[
            { title: 'Total Employees', value: stats.total,   icon: <People />,         gradient: 'linear-gradient(135deg,#1a237e,#283593)', sub: 'Active' },
            { title: 'Present',         value: stats.present, icon: <CheckCircle />,    gradient: 'linear-gradient(135deg,#2e7d32,#388e3c)', sub: 'Checked in',     nav: '/attendance' },
            { title: 'Absent',          value: stats.absent,  icon: <Cancel />,         gradient: 'linear-gradient(135deg,#c62828,#d32f2f)', sub: 'Not checked in', nav: '/attendance' },
            { title: 'Fine',            value: stats.late,    icon: <AccessTime />,     gradient: 'linear-gradient(135deg,#6a1b9a,#7b1fa2)', sub: 'Late/Early',     nav: '/attendance' },
            { title: 'On Leave/Half Day',        value: `${stats.leave}/${stats.half_day}`,   icon: <BeachAccess />,    gradient: 'linear-gradient(135deg,#e65100,#f57c00)', sub: 'Approved',       nav: '/attendance' },
            { title: 'Week Off',        value: stats.weekOff, icon: <EventBusy />,      gradient: 'linear-gradient(135deg,#00695c,#00796b)', sub: 'Day off',        nav: '/attendance' },
            { title: 'Pending Leaves',  value: pendingLeaves.length, icon: <HourglassEmpty />, gradient: 'linear-gradient(135deg,#37474f,#546e7a)', sub: 'Awaiting approval', nav: '/leave' },
            { title: 'Departments',     value: deptCount,     icon: <Business />,       gradient: 'linear-gradient(135deg,#0277bd,#0288d1)', sub: 'Active depts',   nav: '/departments' },
          ].map(s => (
            <Grid item xs={6} sm={3} md={3} key={s.title}>
              <StatCard title={s.title} value={s.value} icon={s.icon} gradient={s.gradient}
                sub={s.sub} loading={statsLoading} onClick={s.nav ? () => navigate(s.nav) : undefined} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Dept Attendance */}
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={800} color="#1a237e">Department Attendance</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(selectedDate).format('DD MMM YYYY')} · Present, Absent, Fine, Half Day, Leave, Week Off
                    </Typography>
                  </Box>
                  <Chip label={`${deptStats.length} dept${deptStats.length !== 1 ? 's' : ''}`} size="small"
                    sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
                </Box>

                {statsLoading ? (
                  [...Array(4)].map((_, i) => (
                    <Box key={i} sx={{ mb: 2.5 }}>
                      <Skeleton variant="text" width="60%" height={20} />
                      <Skeleton variant="rectangular" height={7} sx={{ borderRadius: 4, mt: 0.8 }} />
                    </Box>
                  ))
                ) : deptStats.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Business sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary" fontWeight={500}>No attendance data for this date</Typography>
                    <Typography variant="caption" color="text.secondary">Try selecting a different date</Typography>
                  </Box>
                ) : (
                  deptStats.map(d => (
                    <DeptBar key={d.dept} dept={d.dept} present={d.present} absent={d.absent}
                      late={d.late} half_day={d.half_day} leave={d.leave} weekOff={d.weekOff} total={d.total} />
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Side panel */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={3} direction="column">

              {/* Pending Leaves */}
              <Grid item>
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={800} color="#1a237e">Pending Leaves</Typography>
                        <Typography variant="caption" color="text.secondary">Awaiting approval</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {pendingLeaves.length > 0 && (
                          <Chip label={pendingLeaves.length} size="small"
                            sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                        )}
                        <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/leave')}
                          sx={{ color: '#1a237e', fontWeight: 600, fontSize: 11, minWidth: 0 }}>
                          All
                        </Button>
                      </Box>
                    </Box>

                    {sideLoading ? (
                      [...Array(3)].map((_, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                          <Skeleton variant="circular" width={36} height={36} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="70%" />
                            <Skeleton variant="text" width="50%" />
                          </Box>
                        </Box>
                      ))
                    ) : pendingLeaves.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <BeachAccess sx={{ fontSize: 36, color: '#e8eaf6', mb: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">No pending requests</Typography>
                      </Box>
                    ) : (
                      <List disablePadding>
                        {pendingLeaves.slice(0, 4).map((l, i) => (
                          <React.Fragment key={l.ID || i}>
                            <ListItem disablePadding sx={{ py: 1 }}>
                              <ListItemAvatar>
                                <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg,#e65100,#fb8c00)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                                  {(l.emplyee_name || 'E')[0]}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={<Typography fontWeight={600} fontSize={13}>{l.emplyee_name || `Employee #${l.user_id}`}</Typography>}
                                secondary={
                                  <Typography fontSize={11} color="text.secondary">
                                    {l.leave_type} · {l.start_date ? dayjs(l.start_date).format('DD MMM') : '—'} → {l.end_date ? dayjs(l.end_date).format('DD MMM') : '—'}
                                  </Typography>
                                }
                              />
                              <Chip label="Pending" size="small"
                                sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: 10 }} />
                            </ListItem>
                            {i < Math.min(pendingLeaves.length, 4) - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Upcoming Holidays */}
              <Grid item>
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={800} color="#1a237e">Upcoming Holidays</Typography>
                        <Typography variant="caption" color="text.secondary">Next scheduled holidays</Typography>
                      </Box>
                      <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/holidays')}
                        sx={{ color: '#1a237e', fontWeight: 600, fontSize: 11, minWidth: 0 }}>
                        All
                      </Button>
                    </Box>

                    {sideLoading ? (
                      [...Array(3)].map((_, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                          <Skeleton variant="rounded" width={40} height={40} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="70%" />
                            <Skeleton variant="text" width="40%" />
                          </Box>
                        </Box>
                      ))
                    ) : upcomingHolidays.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CalendarMonth sx={{ fontSize: 36, color: '#e8eaf6', mb: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">No upcoming holidays</Typography>
                      </Box>
                    ) : (
                      <List disablePadding>
                        {upcomingHolidays.map((h, i) => {
                          const daysLeft = dayjs(h.date).diff(dayjs(), 'day');
                          return (
                            <React.Fragment key={h.ID || i}>
                              <ListItem disablePadding sx={{ py: 1 }}>
                                <ListItemAvatar>
                                  <Avatar sx={{
                                    width: 42, height: 42, borderRadius: 2,
                                    background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                                    flexDirection: 'column',
                                  }}>
                                    <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 900, lineHeight: 1 }}>
                                      {dayjs(h.date).format('DD')}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 9, lineHeight: 1 }}>
                                      {dayjs(h.date).format('MMM')}
                                    </Typography>
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={<Typography fontWeight={600} fontSize={13}>{h.title}</Typography>}
                                  secondary={<Typography fontSize={11} color="text.secondary">{dayjs(h.date).format('dddd, YYYY')}</Typography>}
                                />
                                <Chip
                                  label={daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft}d`}
                                  size="small"
                                  sx={{
                                    background: daysLeft === 0 ? '#e8f5e9' : daysLeft <= 3 ? '#fff3e0' : '#e8eaf6',
                                    color: daysLeft === 0 ? '#2e7d32' : daysLeft <= 3 ? '#e65100' : '#1a237e',
                                    fontWeight: 700, fontSize: 10,
                                  }}
                                />
                              </ListItem>
                              {i < upcomingHolidays.length - 1 && <Divider />}
                            </React.Fragment>
                          );
                        })}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          </Grid>
        </Grid>
      </Box>}
    </>
  );
}
